import { useState } from 'react';
import type { JSX } from 'react';

import { useKnownAvailableParams } from '@change-corgi/core/react/router';

import type { PrefetchedDataProps } from 'src/shared/prefetch';
import { withPrefetchedData } from 'src/shared/prefetch';

import type { Sharer } from 'src/app/pages/petitionGamma/details/shared/types';
import type { ReadyToSharePost } from 'src/app/shared/api/readyToShare';
import { getPetitionReadyToSharePostById } from 'src/app/shared/api/readyToShare';

import { ShareHeadlineProvider, useShareForStartersShareHeadline } from '../../hooks/shareHeadline';
import { getSharer } from '../api';
import { CommonPetitionDetails } from '../CommonPetitionDetails';

type PrefetchedData = Readonly<{
	sharer?: Sharer;
	previewLink?: ReadyToSharePost | null;
}>;

export const PetitionDetailsShareForStarters = withPrefetchedData(
	({ prefetchedData }: PrefetchedDataProps<PrefetchedData>): JSX.Element => {
		// this allows us to conserve the sharer after first render
		const [sharer] = useState<Sharer | undefined>(prefetchedData?.sharer);
		const { shareChannel } = useKnownAvailableParams<{
			slug: string;
			shareChannel: string;
		}>();

		const { title, description } = useShareForStartersShareHeadline(shareChannel);

		if (!sharer) {
			return <CommonPetitionDetails />;
		}

		const { previewLink } = prefetchedData || {};
		return (
			<ShareHeadlineProvider sharer={sharer} title={title} description={description} previewLink={previewLink}>
				<CommonPetitionDetails />
			</ShareHeadlineProvider>
		);
	},
	{
		prefetchName: __MODULE_HASH__,
		prefetchDependencies: [CommonPetitionDetails],
		prefetchData: async (context) => {
			const {
				params: { sharerUserId, slug, previewId },
			} = context;

			let previewLink: ReadyToSharePost | null | undefined;
			if (slug && previewId) {
				previewLink = await getPetitionReadyToSharePostById(slug, context.utilityContext, previewId).catch(() => null);
			}

			let sharer: Sharer | undefined;
			if (sharerUserId) {
				sharer = await getSharer(sharerUserId, context.utilityContext).catch(() => sharer);
			}

			return { previewLink, sharer };
		},
	},
);
